import React from 'react';
import { FormattedMessage, intlShape } from '../../util/reactIntl';
import { formatMoney } from '../../util/currency';
import { propTypes } from '../../util/types';
import { resolveLatestProcessName, getProcess } from '../../transactions/transaction';

import { types as sdkTypes } from '../../util/sdkLoader';
import css from './OrderBreakdown.module.css';

/**
 * A component that renders the total price as a line item.
 *
 * @component
 * @param {Object} props
 * @param {propTypes.transaction} props.transaction - The transaction to render
 * @param {boolean} props.isProvider - Whether the provider is the one receiving the commission
 * @param {intlShape} props.intl - The intl object
 * @returns {JSX.Element}
 */
const LineItemTotalPrice = props => {
  const { transaction, isProvider, intl } = props;
  const processName = resolveLatestProcessName(transaction?.attributes?.processName);
  if (!processName) {
    return null;
  }
  const process = getProcess(processName);
  const isCompleted = process.isCompleted(transaction?.attributes?.lastTransition);
  const isRefunded = process.isRefunded(transaction?.attributes?.lastTransition);

  let providerTotalMessageId = 'OrderBreakdown.providerTotalDefault';
  if (isCompleted) {
    providerTotalMessageId = 'OrderBreakdown.providerTotalReceived';
  } else if (isRefunded) {
    providerTotalMessageId = 'OrderBreakdown.providerTotalRefunded';
  }

  const lineItems = transaction?.attributes?.lineItems || [];
  const securityDepositItem = lineItems.find(
    item => item.code === 'line-item/security-deposit' && !item.reversal
  );

  const totalLabel = isProvider ? (
    <FormattedMessage id={providerTotalMessageId} />
  ) : securityDepositItem ? (
    <FormattedMessage id="OrderBreakdown.totalCharged" />
  ) : (
    <FormattedMessage id="OrderBreakdown.total" />
  );

  const payinTotal = transaction?.attributes?.payinTotal;
  let totalPrice = isProvider
    ? transaction?.attributes?.payoutTotal
    : payinTotal;

  if (!isProvider && securityDepositItem && payinTotal?.amount != null) {
    const chargedAmount = payinTotal.amount - securityDepositItem.lineTotal.amount;
    const { Money } = sdkTypes;
    totalPrice = new Money(chargedAmount, payinTotal.currency);
  }

  const formattedTotalPrice = formatMoney(intl, totalPrice);

  return (
    <>
      <hr className={css.totalDivider} />
      <div className={css.lineItemTotal}>
        <div className={css.totalLabel}>{totalLabel}</div>
        <div className={css.totalPrice}>{formattedTotalPrice}</div>
      </div>
    </>
  );
};

export default LineItemTotalPrice;
